'use client';

import Link from 'next/link';
import Image from 'next/image';
import { useEffect, useRef } from 'react';
import { SectLabel } from '@/components/landing/v4-shared';
import { BLOG_SLUG_COPY } from '@/lib/marketing/messaging-bank';
import type { Author } from '@/lib/blog/authors';

const FALLBACK_BLUR =
  'data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHZpZXdCb3g9IjAgMCA0MCAyNyI+PHJlY3Qgd2lkdGg9IjQwIiBoZWlnaHQ9IjI3IiBmaWxsPSIjMTIxNTFmIi8+PC9zdmc+';

export interface ArticleHeroPost {
  title: string;
  slug: string;
  excerpt: string;
  cover_image_url: string | null;
  cover_image_alt: string | null;
  cover_image_credit: string | null;
  published_at: string;
  updated_at: string | null;
  reading_time_minutes: number;
}

/**
 * Article hero — V4 paper-pillar shell with crumbs, eyebrow, h1, excerpt,
 * author + date + reading-time meta row, and a 16:9 cover image.
 *
 * Adds a magnetic-feel cursor on the author link (within ~64px radius)
 * for parity with the industries hero. Fully gated by prefers-reduced-motion.
 */
export function BlogArticleHero({
  post,
  author,
}: {
  post: ArticleHeroPost;
  author: Author;
}) {
  const authorWrapRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const wrap = authorWrapRef.current;
    if (!wrap) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    const link = wrap.querySelector<HTMLAnchorElement>('a[data-magnet]');
    if (!link) return;

    const RADIUS = 64;
    const PULL = 4;
    let raf = 0;

    const onMove = (e: PointerEvent) => {
      const r = link.getBoundingClientRect();
      const cx = r.left + r.width / 2;
      const cy = r.top + r.height / 2;
      const dx = e.clientX - cx;
      const dy = e.clientY - cy;
      const dist = Math.hypot(dx, dy);
      if (dist > RADIUS + Math.max(r.width, r.height) / 2) {
        cancelAnimationFrame(raf);
        raf = requestAnimationFrame(() => {
          link.style.transform = '';
        });
        return;
      }
      const strength = Math.max(
        0,
        1 - dist / (RADIUS + Math.max(r.width, r.height) / 2),
      );
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        link.style.transform = `translate(${(dx / RADIUS) * PULL * strength}px, ${(dy / RADIUS) * PULL * strength}px)`;
      });
    };
    const onLeave = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        link.style.transform = '';
      });
    };
    window.addEventListener('pointermove', onMove);
    link.addEventListener('pointerleave', onLeave);
    return () => {
      window.removeEventListener('pointermove', onMove);
      link.removeEventListener('pointerleave', onLeave);
      cancelAnimationFrame(raf);
    };
  }, []);

  return (
    <>
      <section className="blog-article-hero" aria-labelledby="article-h1">
        <div className="blog-article-hero__inner">
          <nav aria-label="Breadcrumb" className="blog-crumbs">
            <Link href="/blog">Journal</Link>
            <span className="blog-crumbs__sep" aria-hidden="true">/</span>
            <span className="blog-crumbs__here">{post.title}</span>
          </nav>

          <SectLabel color="var(--cyan-glow)">{BLOG_SLUG_COPY.eyebrow ?? 'BizRnR Journal'}</SectLabel>

          <h1 id="article-h1" className="blog-article-hero__h1">
            {post.title}
          </h1>
          <p className="blog-article-hero__excerpt speakable-summary">{post.excerpt}</p>

          <div ref={authorWrapRef} className="blog-article-meta">
            <Link
              href={`/author/${author.slug}`}
              className="blog-article-meta__author"
              data-magnet=""
              rel="author"
            >
              <span className="blog-article-meta__avatar">
                <Image
                  src={author.avatarUrl}
                  alt={`${author.name} avatar`}
                  fill
                  sizes="24px"
                  className="object-cover"
                  unoptimized
                />
              </span>
              <span>{author.name}</span>
            </Link>
            <span className="blog-article-meta__sep" aria-hidden="true">
              ·
            </span>
            <time dateTime={post.published_at}>
              {new Date(post.published_at).toLocaleDateString('en-US', {
                month: 'long',
                day: 'numeric',
                year: 'numeric',
              })}
            </time>
            {post.updated_at && post.updated_at !== post.published_at && (
              <>
                <span className="blog-article-meta__sep" aria-hidden="true">·</span>
                <time dateTime={post.updated_at}>
                  Updated {new Date(post.updated_at).toLocaleDateString('en-US', {
                    month: 'long',
                    day: 'numeric',
                    year: 'numeric',
                  })}
                </time>
              </>
            )}
            <span className="blog-article-meta__sep" aria-hidden="true">
              ·
            </span>
            <span>{post.reading_time_minutes} min read</span>
          </div>
        </div>
      </section>

      {post.cover_image_url && (
        <div className="blog-article-cover">
          <div className="blog-article-cover__inner">
            <Image
              src={post.cover_image_url}
              alt={post.cover_image_alt || post.title}
              fill
              sizes="(max-width: 1024px) 100vw, 1080px"
              className="object-cover"
              placeholder="blur"
              blurDataURL={FALLBACK_BLUR}
              priority
              unoptimized
            />
          </div>
          {post.cover_image_credit && (
            <p className="blog-article-cover__credit">
              Photo: {post.cover_image_credit}
            </p>
          )}
        </div>
      )}
    </>
  );
}
